import { makeStyles, Theme } from '@material-ui/core/styles';
import { Typography, Box } from '@material-ui/core';
import { getNameInCamelCase, getTimeElapsedFromDifferenceInMilli, isLenderOrBackOfficeUserRole } from '@roc/feature-utils';
import classnames from 'classnames';
import { useLoanStore } from '@roc/feature-loans';
import { useCallback, useEffect, useState } from 'react';
import { CircularLoader } from '@roc/ui';
import { sanitizeHtml } from '@roc/ui/utils';
import { CustomCard } from '../customCard';
import { colors } from '../../utils/todoConstants';
import { CustomQuoteRequest } from '@roc/feature-types';
import { observer } from 'mobx-react';

const useStyles = makeStyles((theme: Theme) => ({
  message: {
    alignSelf: 'stretch',
    color: colors.dune,
    fontSize: '14px',
    fontStyle: 'normal',
    lineHeight: '143%',
    fontWeight: 400,
    letterSpacing: '0.25px',
    marginTop: '10px',

    '& *:not(ul):not(ol)': {
      margin: '0 !important',
      padding: '0 !important',
    },

    '& ul, & ol': {
      paddingLeft: '25px',
      marginTop: '8px',
      marginBottom: '8px',
    },

    '& li': {
      marginBottom: '4px',
    }
  },
  timelapse: {
    alignSelf: 'stretch',
    color: colors.mountainMist,
    fontSize: '10px',
    fontStyle: 'normal',
    lineHeight: '266%',
    fontWeight: 400,
    letterSpacing: 'normal',
    textTransform: 'uppercase'
  },
  cardBody: {
    display: 'flex',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  listItem: {
    display: 'flex',
    padding: '0px 16px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    borderBottom: `1px solid ${colors.mercury}`,
  },
  listItemGrayColor: {
    background: colors.soapstone,
  }
}));

type LogEntryProps = {
  value: string;
  label: number;
  millisecondsSinceCreation: number;
};

const ManualQuoteLogCard = observer(({ fullHeight = false }) => {
  const classname = useStyles();
  const { loanStore } = useLoanStore();
  const [logs, setLogs] = useState<LogEntryProps[]>([]);

  useEffect(() => {
    loadLoanChangeRequestLogs();
  }, [loanStore.customQuotes]);

  const loadLoanChangeRequestLogs = useCallback(() => {
    const changeRequests = Array.isArray(loanStore.customQuotes) ? loanStore.customQuotes : [];
    if (!changeRequests.length) {
      setLogs([]);
      return;
    }

    const toPercent = (value?: number | null, fractionDigits = 2) => {
      if (value === null || value === undefined) return undefined;
      const numeric = Number(value);
      if (Number.isNaN(numeric)) return undefined;
      const normalized = numeric > 1 ? numeric : numeric * 100;
      return `${normalized.toFixed(fractionDigits)}%`;
    };

    const entries = changeRequests
      .map((request: CustomQuoteRequest): LogEntryProps | null => {
        if (!request) return null;

        const actorRole = request?.actorRole?.trim();
        const actorName = request?.actorName?.trim();
        const [firstName, lastName] = actorName.split(' ');
        const actor = isLenderOrBackOfficeUserRole(actorRole) ? actorName : getNameInCamelCase(firstName, lastName) ?? 'Unknown';
        const action = (request?.action ?? '').toLowerCase();
        const actionPhrase = (action === 'requested' || action === 'created') || !action
          ? 'requested a'
          : `${action} the request for a`;

        const loanToCost = toPercent(request?.loanToCost) ?? '-';
        const constructionReserve = toPercent(request?.constructionReserve) || '-';
        const totalLoanToCost = toPercent(request?.totalLoanToCost) || '-';
        const arltv = toPercent(request?.arltv) ?? '-';
        const createdDate = request?.createdDate ?? null;
        const millisecondsSinceCreation = request?.millisecondsSinceCreation ?? null;

        const label = createdDate.getTime();

        const value = `${actor} ${actionPhrase} <strong> Loan Cost of ${loanToCost}, Construction Reserve of ${constructionReserve}, Total Loan To Cost of ${totalLoanToCost}, and a ARLTV of ${arltv} <strong/>`;

        return {
          value,
          label,
          millisecondsSinceCreation,
        };
      })
      .filter((entry): entry is LogEntryProps => !!entry);

    setLogs(entries);
    console.log("Setting logs to:", entries);
  }, [loanStore.customQuotes]);

  const [showLoading] = useState(false);

  return (
    <CustomCard
      paperNoShadow
      paperStyle={{
        height: '100%',
        width: fullHeight ? '100%' : 'auto',
      }}
      title="Update Log"
    >
      {showLoading ? <CircularLoader position="relative" thickness={5} color="primary" size={24} /> :
        <Box className={classname.cardBody} style={{ maxHeight: fullHeight ? '100%' : '500px' }}>
          {logs?.length > 0 ? logs.map((update, index) => (
            <Box key={update.label} className={classnames(classname.listItem, {
              [classname.listItemGrayColor]: index % 2 === 0
            })}>
              <div className={classname.message}>
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(update.value) }}></div>
              </div>
              <Typography className={classname.timelapse}>
                {getTimeElapsedFromDifferenceInMilli(update.millisecondsSinceCreation)}
              </Typography>
            </Box>
          )) :
            <Typography className={classname.message}>
              No logs recorded for this loan.
            </Typography>
          }
        </Box>}
    </CustomCard>
  );
});

export default ManualQuoteLogCard;
